import dayjs from 'dayjs';
import { VisitAccessPoint } from 'services/access-points/access-points.service';
import { CreateSingleUsePassInput } from 'services/single-use-passes/single-use-passes.service';
import { CreateVisitorPassInput } from 'services/visitor-passes/visitor-passes.service';
import { computeWindow, EndPreset, StartMode } from 'fragments/passcodes/fragments/visitor-pass-creation/pass-window';
import { formatDayLabel } from 'fragments/passcodes/fragments/visitor-pass-creation/summary-label';
import { formatSince, nowSince } from 'fragments/passcodes/fragments/single-use-pass-creation/date-helpers';
import { capabilitiesFor } from './point-capabilities';
import { deviceOffset } from 'tools/date/site-time';

type Translate = (o: { key: string; extra?: Record<string, unknown> }) => string;

// Lo que el usuario decidió, sin traducir todavía a ninguna credencial. Los
// campos de vencimiento y usos existen siempre aunque el punto elegido no los
// use: la regla 2 pide no perder tipeo al cambiar de puerta, así que se
// conservan y es el payload el que decide si viajan.
export interface VisitDraft {
    point: VisitAccessPoint;
    visitorName: string;
    startMode: StartMode;
    startDate: string; // YYYY-MM-DD, sólo si startMode === 'exact'
    startTime: string; // HH:mm
    endPreset: EndPreset;
    endDate: string;
    endTime: string;
    maxUses?: number;
}

export type VisitPayload =
    | { kind: 'keypad'; input: CreateSingleUsePassInput }
    | { kind: 'reception'; input: CreateVisitorPassInput };

const offsetOf = (draft: VisitDraft): string => draft.point.timezone || deviceOffset();

// Las tres funciones de abajo necesitan la misma ventana. Extraído a un helper
// en vez de repetir el objeto literal: tres copias del mismo mapeo divergen en
// cuanto alguien agregue un campo, y este archivo ya arrastra esa lección — el
// JSON_TYPE del backend existe porque dos copias de un WHERE se desincronizaron.
const windowOf = (draft: VisitDraft) =>
    computeWindow({
        startMode: draft.startMode,
        startDate: draft.startDate,
        startTime: draft.startTime,
        endPreset: draft.endPreset,
        endDate: draft.endDate,
        endTime: draft.endTime,
        offset: offsetOf(draft),
    });

// Inicio en wall-clock del sitio, truncado a la hora en punto: es lo que hace el
// backend con `since` (startDate.Truncate(time.Hour)), así que calcularlo acá
// mantiene el resumen honesto respecto de lo que la cerradura va a hacer.
const startWallClock = (draft: VisitDraft): { date: string; time: string } => {
    if (draft.startMode === 'now') {
        return nowSince(offsetOf(draft));
    }
    return { date: draft.startDate, time: `${draft.startTime.slice(0, 2)}:00:00` };
};

export function buildVisitPayload(draft: VisitDraft): VisitPayload {
    // La rama se decide por la señal canónica (la duración que impone el punto),
    // igual que buildVisitSummary e isDraftInFuture — NO por comparar el kind con
    // un literal. Con un tercer kind, un `kind === 'keypad'` acá lo mandaría en
    // silencio por la rama de reception: payload y endpoint equivocados.
    if (capabilitiesFor(draft.point.kind).fixedHours !== undefined) {
        const w = startWallClock(draft);
        // El "Z" es literal, no UTC: el backend reinterpreta esta hora en el
        // timezone del lock (api/single_use_passes.go:115). Mandar el offset real
        // acá correría el código hasta 4 horas en RD.
        return {
            kind: 'keypad',
            input: { lock_id: draft.point.id, label: draft.visitorName.trim(), since: formatSince(w.date, w.time) },
        };
    }

    const w = windowOf(draft);
    return {
        kind: 'reception',
        input: {
            site_id: draft.point.id,
            visitor_name: draft.visitorName.trim(),
            nbf: w.nbf,
            exp: w.exp,
            max_uses: draft.maxUses,
        },
    };
}

export function buildVisitSummary(draft: VisitDraft, translate: Translate): string {
    const caps = capabilitiesFor(draft.point.kind);

    if (caps.fixedHours !== undefined) {
        const w = startWallClock(draft);
        const from = w.time.slice(0, 5);
        // No se usa windowEndLabel (que trae su propio 6h hardcodeado): acá la
        // duración sale de caps.fixedHours, la misma tabla que usa isDraftInFuture,
        // para que el resumen y la validación de vencimiento nunca diverjan si el
        // hardware cambia de duración.
        const to = dayjs(`${w.date}T${w.time}`).add(caps.fixedHours, 'hour').format('HH:00');
        return draft.startMode === 'now'
            ? translate({ key: 'visit-access.summary-keypad-today', extra: { from, to } })
            : translate({
                  key: 'visit-access.summary-keypad-day',
                  extra: { day: formatDayLabel(w.date), from, to },
              });
    }

    const w = windowOf(draft);
    const isDominantCase = draft.startMode === 'now' && draft.endPreset === 'p1';
    const base = isDominantCase
        ? translate({ key: 'visit-access.summary-reception-today' })
        : translate({
              key: 'visit-access.summary-reception-range',
              extra: {
                  from: formatDayLabel(w.startDate),
                  fromTime: draft.startMode === 'now' ? 'ahora' : draft.startTime,
                  to: formatDayLabel(w.endDate),
                  toTime: draft.endPreset === 'exact' ? draft.endTime : '23:59',
              },
          });

    if (draft.maxUses === undefined) {
        return base;
    }
    const uses = translate({
        key: draft.maxUses === 1 ? 'visit-access.uses-count' : 'visit-access.uses-count-plural',
        extra: { count: draft.maxUses },
    });
    return `${base} · ${uses}`;
}

// Rechaza lo que nacería vencido. El backend del passcode no lo valida (sólo
// arma nbf+6h), así que sin este chequeo se emitiría un código muerto y el
// usuario se enteraría recién cuando la visita no pudiera entrar.
export function isDraftInFuture(draft: VisitDraft, now: dayjs.Dayjs = dayjs()): boolean {
    const caps = capabilitiesFor(draft.point.kind);
    if (caps.fixedHours !== undefined) {
        const w = startWallClock(draft);
        const end = dayjs(`${w.date}T${w.time}${offsetOf(draft)}`).add(caps.fixedHours, 'hour');
        return end.valueOf() > now.valueOf();
    }
    const w = windowOf(draft);
    return new Date(w.exp).getTime() > now.valueOf() && new Date(w.exp).getTime() > new Date(w.nbf).getTime();
}
